import { useEffect, useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import Starfield from "@/components/Starfield";
import Wordmark from "@/components/Wordmark";
import OrbitalInstrument from "@/components/OrbitalInstrument";
import Marquee from "@/components/Marquee";
import { PrimaryCTA } from "@/components/CosmicButton";
import { track, EVENTS } from "@/lib/analytics";

const EASE = [0.16, 1, 0.3, 1];

const lineReveal = {
    hidden: { y: "112%" },
    show: (i) => ({
        y: "0%",
        transition: { duration: 0.9, ease: EASE, delay: 0.15 + i * 0.13 },
    }),
};

const rise = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.15 },
    transition: { duration: 0.7, ease: EASE },
};

const STEPS = [
    {
        n: "01",
        t: "NAME THE MOMENT",
        d: "A real situation that hit a nerve. Not hypothetical. Not fluffy.",
    },
    {
        n: "02",
        t: "SPOT THE DEFAULT",
        d: "What you actually did — your first instinct, not your best behavior.",
    },
    {
        n: "03",
        t: "FIND IT IN THE CHART",
        d: "The parts of your birth chart worth exploring, and why they might be loud.",
    },
];

export default function Landing() {
    useEffect(() => {
        document.title = "WHY THE FUCK DO I DO THAT? | SacredSpace";
        track(EVENTS.DECODER_LANDING_VIEW, { decoder: "emotional-reactions" });
    }, []);

    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: heroRef,
        offset: ["start start", "end start"],
    });
    const driftY = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const driftFade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

    const tiltX = useMotionValue(0);
    const tiltY = useMotionValue(0);
    const rotateX = useSpring(tiltX, { stiffness: 60, damping: 16 });
    const rotateY = useSpring(tiltY, { stiffness: 60, damping: 16 });
    const onTilt = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        tiltX.set(-((e.clientY - r.top) / r.height - 0.5) * 7);
        tiltY.set(((e.clientX - r.left) / r.width - 0.5) * 7);
    };

    return (
        <main className="relative min-h-dvh overflow-hidden bg-ink text-slate-200">
            <Starfield density={80} />

            <header className="absolute inset-x-0 top-0 z-20 flex items-center justify-between px-5 py-6 sm:px-8">
                <Wordmark />
                <p className="hidden font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 sm:block">
                    The Pattern Decoder · V1
                </p>
            </header>

            <section
                ref={heroRef}
                onMouseMove={onTilt}
                className="relative z-10 mx-auto grid min-h-[100svh] w-full max-w-6xl items-center gap-10 px-5 pb-16 pt-28 sm:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:gap-6 lg:pt-20"
            >
                <div>
                    <p className="mb-6 font-mono text-[11px] uppercase tracking-[0.32em] text-celestial-blue">
                        SacredSpace · A behavior-first cosmic pattern decoder
                    </p>
                    <h1 className="font-sans text-[clamp(2.9rem,10.5vw,6.4rem)] font-bold uppercase leading-[0.95] tracking-tight">
                        <span className="block overflow-hidden pb-1">
                            <motion.span
                                className="block"
                                variants={lineReveal}
                                custom={0}
                                initial="hidden"
                                animate="show"
                            >
                                WHY THE
                            </motion.span>
                        </span>
                        <span className="block overflow-hidden pb-2">
                            <motion.span
                                className="block"
                                variants={lineReveal}
                                custom={1}
                                initial="hidden"
                                animate="show"
                            >
                                <em className="font-display text-[1.06em] normal-case italic text-celestial-gold">
                                    Fuck
                                </em>
                            </motion.span>
                        </span>
                        <span className="block overflow-hidden">
                            <motion.span
                                className="block"
                                variants={lineReveal}
                                custom={2}
                                initial="hidden"
                                animate="show"
                            >
                                DO I DO THAT?
                            </motion.span>
                        </span>
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.65, duration: 0.7, ease: EASE }}
                        className="mt-7 max-w-md text-lg leading-relaxed text-slate-300"
                    >
                        You already know what you do. Let's figure out what
                        might be underneath it — and where to start looking in
                        your birth chart.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.8, duration: 0.7, ease: EASE }}
                        className="mt-10"
                    >
                        <PrimaryCTA to="/intro" testid="landing-cta-call-me-out">
                            Call me out
                        </PrimaryCTA>
                        <p
                            data-testid="landing-supporting-line"
                            className="mt-5 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500"
                        >
                            About 2 minutes. No email required to see your result.
                        </p>
                    </motion.div>
                </div>

                <motion.div
                    style={{ y: driftY, opacity: driftFade }}
                    className="hidden lg:block"
                >
                    <motion.div
                        style={{ rotateX, rotateY, transformPerspective: 900 }}
                    >
                        <OrbitalInstrument />
                    </motion.div>
                </motion.div>
            </section>

            <div className="relative z-10 lg:hidden">
                <div className="mx-auto -mt-6 mb-10 w-full max-w-[300px] px-5">
                    <OrbitalInstrument />
                </div>
            </div>

            <div className="relative z-10">
                <Marquee />
            </div>

            <section className="relative z-10 mx-auto w-full max-w-6xl px-5 py-24 sm:px-8">
                <motion.p {...rise} className="font-mono text-[11px] uppercase tracking-[0.32em] text-celestial-blue">
                    How this works
                </motion.p>
                <div className="mt-10 grid gap-8 sm:grid-cols-3">
                    {STEPS.map((s, i) => (
                        <motion.div
                            key={s.n}
                            {...rise}
                            transition={{ ...rise.transition, delay: i * 0.12 }}
                            className="border-t border-white/10 pt-6"
                        >
                            <p className="font-mono text-xs text-celestial-gold">{s.n}</p>
                            <h2 className="mt-4 text-lg font-bold uppercase tracking-[0.12em]">
                                {s.t}
                            </h2>
                            <p className="mt-3 text-[15px] leading-relaxed text-slate-400">
                                {s.d}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </section>

            <section className="relative z-10 border-t border-white/10">
                <div className="mx-auto w-full max-w-3xl px-5 py-24 text-center sm:px-8">
                    <motion.p {...rise} className="font-display text-3xl italic leading-snug text-slate-100 sm:text-4xl">
                        “Holy shit. I do that.”
                    </motion.p>
                    <motion.p
                        {...rise}
                        transition={{ ...rise.transition, delay: 0.1 }}
                        className="mt-4 font-mono text-[11px] uppercase tracking-[0.28em] text-slate-500"
                    >
                        Wait… where does THAT show up in my chart?
                    </motion.p>
                    <motion.div {...rise} transition={{ ...rise.transition, delay: 0.2 }} className="mt-10">
                        <PrimaryCTA to="/intro" testid="landing-cta-call-me-out-footer">
                            Call me out
                        </PrimaryCTA>
                    </motion.div>
                </div>
            </section>

            <footer className="relative z-10 border-t border-white/10 px-5 py-8 sm:px-8">
                <div className="mx-auto flex w-full max-w-6xl flex-col items-center justify-between gap-3 sm:flex-row">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                        © SacredSpace: Through The Cosmic Lens
                    </p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-600">
                        Astrology is a lens, not a diagnosis.
                    </p>
                </div>
            </footer>
        </main>
    );
}
